// src/components/common/MaintenanceChecker.js
import React, { useEffect, useState, useRef } from 'react';
import MaintenancePage from '../../pages/maintenance';
import api from '../../store/api/base';

const POLL_INTERVAL_MS  = 5 * 60 * 1000; // 5 minutes (was 30s — too aggressive)
const BACKOFF_429_MS    = 10 * 60 * 1000; // back off 10 min after a 429

const MaintenanceChecker = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo]     = useState(null);
  const [isChecking, setIsChecking]               = useState(true);

  // Guard against React strict-mode double-invoke firing two simultaneous requests
  const inFlight = useRef(false);
  const timerRef = useRef(null);

  const checkMaintenanceStatus = async () => {
    if (inFlight.current) return;
    inFlight.current = true;

    try {
      const response = await api.get('/maintenance/status');

      if (response.data.status === 'success') {
        const { isActive, title, message, estimatedEnd, reason } = response.data.data;
        setIsMaintenanceMode(isActive);
        setMaintenanceInfo({ title, message, estimatedEnd, reason });
      }
    } catch (error) {
      if (error.response?.status === 503) {
        // Server is in maintenance mode
        setIsMaintenanceMode(true);
        setMaintenanceInfo(error.response?.data?.data || {});
      } else if (error.response?.status === 429) {
        // Rate limited — reschedule with a longer backoff, don't block the app
        console.warn('MaintenanceChecker: rate limited (429), backing off for 10 min');
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          inFlight.current = false;
          checkMaintenanceStatus();
        }, BACKOFF_429_MS);
        // Don't set isChecking to false — fall through to finally below
      } else {
        // Any other error: fail open (allow access)
        console.warn('MaintenanceChecker: check failed, allowing access');
        setIsMaintenanceMode(false);
      }
    } finally {
      inFlight.current = false;
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkMaintenanceStatus();

    // Poll every 5 minutes — enough to catch maintenance windows without hammering
    timerRef.current = setInterval(checkMaintenanceStatus, POLL_INTERVAL_MS);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          <img
            src="/assets/logo/klm-logo.png"
            alt="KLMKart"
            className="h-20 w-auto animate-pulse"
          />
        </div>
      </div>
    );
  }

  if (isMaintenanceMode) {
    return <MaintenancePage maintenanceInfo={maintenanceInfo} />;
  }

  return children;
};

export default MaintenanceChecker;